import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Clock, ExternalLink, Loader2, Rocket } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import { dataDictionaryApi } from './dataDictionaryApi';
import { errorMessage, formatDateTime, formatDuration, formatNumber, formatRelative } from './dataDictionaryUtils';

const ACTIVE_POLL_MS = 5000;

/**
 * Tracks the data_dictionary_refresh_prod job: pending (unpublished) row count, active run, history.
 * Warehouse unpublished-row count runs on load, after edits (`refresh`), and when a publish finishes.
 * While a run is active, poll job status only (no warehouse count).
 */
export function usePublishStatus(enabled) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const timerRef = useRef(null);
  const prevActiveRef = useRef(null);
  const genRef = useRef(0);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const tick = useCallback(async ({ includePending = true } = {}) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const gen = ++genRef.current;
    let data = null;
    try {
      data = await dataDictionaryApi.publishStatus({ includePending });
      if (gen !== genRef.current) return;
      setStatus((prev) => {
        if (!includePending && prev) {
          return { ...data, pending_count: prev.pending_count, pending_since: prev.pending_since };
        }
        return data;
      });
      setError('');
    } catch (e) {
      if (gen !== genRef.current) return;
      setError(errorMessage(e, 'Could not load publish status'));
    }
    if (!enabledRef.current) return;
    const active = Boolean(data?.active_run);
    // A run we were watching just finished → tell the user how it went.
    if (prevActiveRef.current && !active && data?.last_run) {
      if (data.last_run.outcome === 'success') {
        toast.success(`Published to segment builder · run ${data.last_run.run_id} finished in ${formatDuration(data.last_run.duration_ms)}`);
      } else if (data.last_run.outcome === 'failed') {
        toast.error(`Publish run ${data.last_run.run_id} failed${data.last_run.state_message ? `: ${data.last_run.state_message}` : ''}`);
      }
      prevActiveRef.current = active;
      if (!includePending) {
        timerRef.current = setTimeout(() => tick({ includePending: true }), 0);
        return;
      }
    }
    prevActiveRef.current = active;
    if (active) {
      timerRef.current = setTimeout(() => tick({ includePending: false }), ACTIVE_POLL_MS);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    tick({ includePending: true });
    return () => {
      genRef.current += 1;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, tick]);

  const refresh = useCallback(() => tick({ includePending: true }), [tick]);

  return { status, error, refresh };
}

function Pill({ tone, icon: Icon, children, spin = false }) {
  const tones = {
    ok: 'bg-emerald-50 text-emerald-800',
    warn: 'bg-amber-50 text-amber-800',
    info: 'bg-primary-50 text-primary-700',
    danger: 'bg-red-50 text-red-800',
    muted: 'bg-[#E8EEF5] text-[#71717A]',
  };
  return (
    <span className={`inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-medium ${tones[tone] || tones.muted}`}>
      <Icon size={13} className={spin ? 'animate-spin' : ''} aria-hidden />
      {children}
    </span>
  );
}

function PendingChangesDialog({ isOpen, onClose, onConfirm, publishing }) {
  const [pending, setPending] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setPending(null);
    setError('');
    dataDictionaryApi
      .pendingChanges(100)
      .then((d) => !cancelled && setPending(d))
      .catch((e) => !cancelled && setError(errorMessage(e, 'Could not load pending changes')));
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const total = pending?.total ?? 0;
  return (
    <Modal isOpen={isOpen} onClose={publishing ? () => {} : onClose} onEnter={!publishing && pending ? onConfirm : undefined} className="w-full max-w-xl p-6">
      <h2 className="font-display pr-8 text-lg font-bold tracking-[-0.01em] text-[#18181B]">
        {pending ? `Publish ${formatNumber(total)} change${total === 1 ? '' : 's'} to the segment builder?` : 'Publish to the segment builder?'}
      </h2>
      <p className="mt-1 text-[13px] text-[#71717A]">
        Runs the <code className="rounded bg-[#E8EEF5] px-1 font-mono text-[12px]">data_dictionary_refresh_prod</code> job and reloads search. Usually takes a couple of minutes; the segment builder stays available meanwhile.
      </p>

      <div className="mt-4 max-h-72 overflow-auto rounded-lg border border-hairline">
        {error ? (
          <p className="px-3 py-3 text-sm text-red-600">{error}</p>
        ) : !pending ? (
          <p className="inline-flex items-center gap-2 px-3 py-3 text-sm text-[#71717A]">
            <Loader2 size={14} className="animate-spin" aria-hidden /> Loading changes…
          </p>
        ) : pending.hits.length === 0 ? (
          <p className="px-3 py-3 text-sm text-[#71717A]">No unpublished edits since the last successful run — publishing will simply re-sync the catalog.</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-[#F8FAFC] text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#71717A]">
              <tr>
                <th className="h-8 px-3">Column</th>
                <th className="h-8 px-3">Table</th>
                <th className="h-8 px-3">Edited</th>
              </tr>
            </thead>
            <tbody>
              {pending.hits.map((h) => (
                <tr key={`${h.schema}.${h.table_name}.${h.column_name}`} className="border-t border-[#EEF2F7]">
                  <td className="px-3 py-1.5">
                    <div className="flex flex-col leading-4">
                      <span className="font-medium">{h.display_name || h.column_name}</span>
                      <span className="font-mono text-[11px] text-[#71717A]">{h.column_name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[12px] text-[#71717A]">{h.schema}.{h.table_name}</td>
                  <td className="px-3 py-1.5 text-[#71717A]">{formatRelative(h.updated_at)}</td>
                </tr>
              ))}
              {total > pending.hits.length ? (
                <tr className="border-t border-[#EEF2F7]">
                  <td colSpan={3} className="px-3 py-2 text-xs text-[#71717A]">…and {formatNumber(total - pending.hits.length)} more</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <span className="text-xs text-[#71717A]">Edits are already saved to the dictionary; publishing makes them live for users.</span>
        <div className="flex shrink-0 gap-2">
          <button type="button" onClick={onClose} disabled={publishing} className="inline-flex h-8 shrink-0 items-center whitespace-nowrap rounded-md border border-hairline bg-surface px-3 text-[13px] font-medium text-[#18181B] hover:bg-[#E8EEF5] disabled:opacity-50">
            Later
          </button>
          <button type="button" onClick={onConfirm} disabled={publishing || !pending} className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-[#0062FB] px-3 text-[13px] font-medium text-white hover:bg-[#0052E0] disabled:opacity-50">
            {publishing ? <Loader2 size={13} className="animate-spin" aria-hidden /> : <Rocket size={13} aria-hidden />}
            Publish
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RunningFor({ startIso }) {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!startIso) return null;
  return <>{formatDuration(Date.now() - new Date(startIso).getTime())}</>;
}

/** Header pill + Publish button. */
export default function PublishControls({ status, error, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const active = status?.active_run;
  const last = status?.last_run;
  const pending = status?.pending_count;
  const lastSuccess = status?.last_success;

  const publish = async () => {
    setPublishing(true);
    try {
      const res = await dataDictionaryApi.apply();
      toast.success(`Publish started · run ${res?.run_id ?? ''}`.trim());
      setOpen(false);
      onRefresh?.();
    } catch (e) {
      toast.error(errorMessage(e, 'Publish failed to start'));
      onRefresh?.();
    } finally {
      setPublishing(false);
    }
  };

  let pill;
  if (error && !status) {
    pill = (
      <Pill tone="muted" icon={AlertTriangle}>
        Publish status unavailable
      </Pill>
    );
  } else if (active) {
    pill = (
      <Pill tone="info" icon={Loader2} spin>
        Publishing · running <RunningFor startIso={active.start_time} />
        {active.run_page_url ? (
          <a href={active.run_page_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline">
            run {active.run_id} <ExternalLink size={10} aria-hidden />
          </a>
        ) : null}
      </Pill>
    );
  } else if (last && last.outcome === 'failed' && (!lastSuccess || new Date(last.start_time) > new Date(lastSuccess.start_time))) {
    pill = (
      <Pill tone="danger" icon={AlertTriangle}>
        Publish failed
        {last.run_page_url ? (
          <a href={last.run_page_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline">
            view run <ExternalLink size={10} aria-hidden />
          </a>
        ) : null}
        {pending ? <span>· {formatNumber(pending)} unpublished</span> : null}
      </Pill>
    );
  } else if (pending) {
    pill = (
      <Pill tone="warn" icon={Clock}>
        {formatNumber(pending)} unpublished change{pending === 1 ? '' : 's'}
      </Pill>
    );
  } else if (lastSuccess) {
    pill = (
      <Pill tone="ok" icon={Check}>
        Up to date · published {formatDateTime(lastSuccess.end_time || lastSuccess.start_time)}
      </Pill>
    );
  } else if (status) {
    pill = (
      <Pill tone="muted" icon={Clock}>
        Never published
      </Pill>
    );
  } else {
    pill = (
      <Pill tone="muted" icon={Loader2} spin>
        Checking publish status…
      </Pill>
    );
  }

  const disabled = Boolean(active) || (!status && !error);
  const label = active ? 'Publishing…' : last?.outcome === 'failed' && pending ? 'Retry publish' : 'Publish to segment builder';

  return (
    <div className="flex flex-wrap items-center justify-end gap-3">
      {pill}
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="inline-flex h-10 items-center gap-2 rounded-md border border-[#0062FB] bg-[#0062FB] px-4 text-sm font-medium text-white transition-colors hover:border-[#0052E0] hover:bg-[#0052E0] disabled:pointer-events-none disabled:opacity-50"
        title={pending === 0 ? 'Nothing unpublished — you can still re-sync the catalog' : undefined}
      >
        <Rocket size={16} aria-hidden />
        {label}
      </button>
      <PendingChangesDialog isOpen={open} onClose={() => setOpen(false)} onConfirm={publish} publishing={publishing} />
    </div>
  );
}
